import React, {
  useState, useEffect, useMemo, useRef
} from 'react';
import PropTypes from 'prop-types';
import { Typography } from '@one-thd/sui-atomic-components';
import { useLazyDataModel } from '@thd-nucleus/data-sources';
import { useStore } from '@thd-nucleus/experience-context';

export const BulkProductsSummary = ({ products }) => {
  const [loadedProductsData, setLoadedProductsData] = useState([]);
  const { storeId, storeZip } = useStore({ varnish: true });
  const fetchedItemIds = useRef(new Set());

  const [
    fetchProducts, { data: productsData, loading: productsLoading, error }
  ] = useLazyDataModel('products', {});

  const productIds = useMemo(() => {
    return products?.filter((product) => product.itemId).map((product) => product.itemId).sort().join(',');
  }, [products]);

  useEffect(() => {
    if (products?.length > 0) {
      const firstThreeProducts = products.slice(0, 3);
      // Only fetch catalog items (those with itemId), skip intents
      const itemIds = firstThreeProducts
        .filter((product) => product.itemId)
        .map((product) => product.itemId);
      const needsFetch = itemIds.length > 0 && itemIds.some((id) => !fetchedItemIds.current.has(id));

      if (needsFetch && !productsLoading) {
        fetchProducts({
          variables: {
            itemIds,
            storeId,
            zipCode: storeZip
          }
        });
        itemIds.forEach((id) => fetchedItemIds.current.add(id));
      }
    }
  }, [productIds, storeId, storeZip, fetchProducts, productsLoading]);

  useEffect(() => {
    if (productsData?.products) {
      setLoadedProductsData(productsData.products);
    }
  }, [productsData]);

  useEffect(() => {
    fetchedItemIds.current.clear();
    setLoadedProductsData([]);
  }, [productIds]);

  const enhancedProducts = useMemo(() => {
    return products.map((product) => {
      const productDetails = loadedProductsData.find((detail) => detail.itemId === product.itemId);
      return {
        ...product,
        identifiers: productDetails?.identifiers || product.identifiers,
        pricing: productDetails?.pricing || product.pricing
      };
    });
  }, [products, loadedProductsData]);

  const productCount = products?.length || 0;

  if (productCount === 0) {
    return null;
  }

  if (productsLoading && loadedProductsData.length === 0) {
    return (
      <div className="sui-px-6 sui-pb-6">
        <Typography
          weight="bold"
          className="sui-text-primary sui-mb-4"
        >
          Loading product details...
        </Typography>
      </div>
    );
  }

  return (
    <div className="sui-pb-6 sui-px-2 sui-border-primary
                 sui-border-b-2 sui-border-solid"
    >
      <Typography
        weight="bold"
        className="sui-text-primary sui-mb-4"
      >
        Add {productCount} items
      </Typography>
      <ul className="sui-mt-1 sui-list-disc sui-list-inside sui-overflow-hidden">
        {enhancedProducts.slice(0, 3).map((product) => (
          <li
            key={product.itemId || product.intent}
            className="sui-overflow-hidden sui-text-ellipsis sui-truncate"
          >
            <Typography component="span" weight="bold">
              {product?.identifiers?.brandName}
            </Typography>
            &nbsp;
            <Typography component="span" truncate>
              {product?.intent || product?.identifiers?.productLabel}
            </Typography>
          </li>
        ))}
      </ul>
      { enhancedProducts.length > 3 && (
        <div className="sui-ml-2">
          <Typography>
            +{enhancedProducts.length - 3} more
          </Typography>
        </div>
      )}
    </div>
  );
};

BulkProductsSummary.propTypes = {
  products: PropTypes.arrayOf(PropTypes.shape({
    itemId: PropTypes.string,
    intent: PropTypes.string,
    identifiers: PropTypes.shape({
      brandName: PropTypes.string,
      productLabel: PropTypes.string
    }),
    pricing: PropTypes.shape({
      value: PropTypes.number
    })
  }))
};

BulkProductsSummary.defaultProps = {
  products: []
};